import { Link } from "react-router-dom";
import { classNames } from "primereact/utils";
import { Icon, IconType } from "../../../common/icons/Icon";
import { RenderWhen } from "../../../common/infrastructure/components/RenderWhen";
import { routePaths } from "../../../common/infrastructure/routes/routePaths";
import { AiComposerState } from "../hooks/useAiComposer";
import { aiAssistantTexts } from "../models/aiAssistantModels";
import styles from "./AiComposer.module.scss";

interface IAiComposerProps {
  composer: AiComposerState;
  isSending: boolean;
  /** The note and the link under the box; hidden while a phone keyboard takes the room. */
  hideFooter?: boolean;
  onStop: () => void;
}

export const AiComposer = ({ composer, isSending, hideFooter = false, onStop }: IAiComposerProps) => {
  return (
    <div className={styles.root}>
      <RenderWhen condition={composer.attachments.length > 0}>
        <div className={styles.attachments}>
          {composer.attachments.map((attachment) => (
            <div key={attachment.localId} className={styles.attachment}>
              {attachment.previewUrl ? (
                <img className={styles.attachment_image} src={attachment.previewUrl} alt={attachment.fileName} />
              ) : (
                <div className={styles.attachment_placeholder} />
              )}
              <RenderWhen condition={attachment.status !== "uploaded"}>
                <div
                  className={styles.attachment_progress}
                  role="progressbar"
                  aria-label={`Качване на ${attachment.fileName}`}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={Math.round(attachment.progress * 100)}
                >
                  <span className={styles.spinner} />
                  <span className={styles.attachment_bar} style={{ width: `${Math.round(attachment.progress * 100)}%` }} />
                </div>
              </RenderWhen>
              <button
                type="button"
                className={styles.attachment_remove}
                onClick={() => composer.removeAttachment(attachment.localId)}
                aria-label={`${aiAssistantTexts.removeImage} ${attachment.fileName}`}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      </RenderWhen>

      <RenderWhen condition={composer.showCharCounter}>
        <span
          className={classNames(styles.counter, { [styles.counter_full]: composer.charCount >= composer.maxChars })}
          aria-live="polite"
        >
          {composer.charCount}/{composer.maxChars}
        </span>
      </RenderWhen>

      <div className={styles.row}>
        <button
          type="button"
          className={styles.icon_btn}
          onClick={composer.openFilePicker}
          disabled={!composer.canAttach}
          aria-label={aiAssistantTexts.attachImage}
          title={aiAssistantTexts.attachImage}
        >
          <Icon type={IconType.Paperclip} width="18px" height="18px" color="currentColor" />
        </button>

        <div className={styles.field}>
          <textarea
            ref={composer.textareaRef}
            className={styles.textarea}
            value={composer.text}
            onChange={composer.handleChange}
            onKeyDown={composer.handleKeyDown}
            onPaste={composer.handlePaste}
            placeholder={aiAssistantTexts.inputPlaceholder}
            aria-label={aiAssistantTexts.inputPlaceholder}
            maxLength={composer.maxChars}
            rows={1}
          />
        </div>

        {isSending ? (
          <button
            type="button"
            className={classNames(styles.send_btn, styles.send_btn_stop)}
            onClick={onStop}
            aria-label={aiAssistantTexts.stop}
            title={aiAssistantTexts.stop}
          >
            <Icon type={IconType.Stop} width="18px" height="18px" color="currentColor" />
          </button>
        ) : (
          <button
            type="button"
            className={styles.send_btn}
            onClick={() => void composer.submit()}
            disabled={!composer.canSend}
            aria-label={aiAssistantTexts.send}
            title={aiAssistantTexts.send}
          >
            <Icon type={IconType.SendIcon} width="20px" height="20px" color="currentColor" />
          </button>
        )}
      </div>

      <RenderWhen condition={!hideFooter}>
        <div className={styles.footer}>
          <span>
            <span className={styles.footer_full}>{aiAssistantTexts.disclaimer}</span>
            <span className={styles.footer_short}>{aiAssistantTexts.disclaimerShort}</span>
          </span>
          <span className={styles.footer_separator} aria-hidden="true">
            ·
          </span>
          <Link className={styles.footer_link} to={routePaths.supportNew}>
            <span className={styles.footer_full}>{aiAssistantTexts.humanLink}</span>
            <span className={styles.footer_short}>{aiAssistantTexts.humanLinkShort}</span>
          </Link>
        </div>
      </RenderWhen>

      <input
        ref={composer.fileInputRef}
        className={styles.file_input}
        type="file"
        accept="image/*,.heic,.heif"
        multiple
        onChange={composer.handleFileInputChange}
        tabIndex={-1}
        aria-hidden="true"
      />
    </div>
  );
};
